// Sign in with Google, Microsoft or Apple: the three account types, their names on the screens, and the messages shown
// when a sign-in can't go ahead (server/src/lib/sso.ts sends the code; the sign-in screens show the message).

export const SIGN_IN_PROVIDERS = ["google", "microsoft", "apple"] as const;
export type SignInProvider = (typeof SIGN_IN_PROVIDERS)[number];

export const PROVIDER_NAME: Record<SignInProvider, string> = { google: "Google", microsoft: "Microsoft", apple: "Apple" };

export const isSignInProvider = (v: unknown): v is SignInProvider => SIGN_IN_PROVIDERS.includes(v as SignInProvider);

/** What the person was doing when they pressed the button. */
export type SignInPurpose = "signin" | "invite" | "link" | "unlock";

/** The messages for each refusal, in plain words. {provider} and {email} are filled in. */
export const SSO_ERRORS: Record<string, string> = {
  NOT_SET_UP: "Signing in with {provider} isn't switched on. Sign in with your email and password.",
  CANCELLED: "Signing in with {provider} was cancelled. Try again, or sign in with your email and password.",
  EXPIRED: "That took too long, so the sign-in was stopped. Press the button again.",
  FAILED: "{provider} didn't confirm the sign-in. Try again in a moment, or sign in with your email and password.",
  NO_ACCOUNT: "No one at Falcon uses {email}. Sign in with the account your organisation invited, or ask your Admin to invite this address.",
  NOT_VERIFIED: "{provider} hasn't confirmed that {email} is yours, so Falcon can't use it to sign you in. Accept your invitation with {provider}, or link {provider} in My Account after signing in with your password.",
  WRONG_EMAIL: "That {provider} account uses {email}, which isn't the address your Falcon account uses. Choose the {provider} account with the same email address.",
  NOT_LINKED: "That {provider} account isn't linked to your Falcon account. Use your password or two-factor code instead.",
  ALREADY_LINKED: "That {provider} account is already linked to another Falcon account.",
  DISABLED: "Your Falcon account is switched off. Ask your organisation's Admin.",
  LOCKED: "Too many wrong attempts on your account. Try again in a few minutes.",
  LINK_EXPIRED: "This invitation has expired or has already been used. Ask for a new one.",
  SIGNED_OUT: "Your sign-in has ended. Sign in again, then try once more.",
};

export function ssoErrorText(code: string, provider: string, email = ""): string {
  const name = isSignInProvider(provider) ? PROVIDER_NAME[provider] : "that account";
  return (SSO_ERRORS[code] || SSO_ERRORS.FAILED).replaceAll("{provider}", name).replaceAll("{email}", email || "that email address");
}

/** Apple's "Hide my email" addresses: accepted when the person accepts an invitation or links Apple while signed in. */
export const isApplePrivateRelay = (email: string) => /@privaterelay\.appleid\.com$/i.test(email);
